export const fmtEUR = (value) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
        Number(value || 0)
    );

export const toNum = (value) => {
    const normalized = typeof value === "string" ? value.replace(",", ".") : value;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const durationHours = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const s = sh * 60 + (sm || 0);
    const e = eh * 60 + (em || 0);
    const diff = e - s;
    return diff > 0 ? +(diff / 60).toFixed(2) : 0;
};

export const createEmptyOrder = () => ({
    id: null,
    version: null,
    title: "",
    customer: "",
    customerId: "",
    invoiceIds: [],
    performanceDays: [],
    materials: [],
});

export const mapDtoToOrder = (dto) => ({
    id: dto?.id ?? null,
    version: dto?.version ?? null,
    title: dto?.title ?? "",
    customer: "",
    customerId: dto?.customerId ?? "",
    invoiceIds: Array.isArray(dto?.invoiceIds) ? dto.invoiceIds : [],
    performanceDays: Array.isArray(dto?.timeEntries)
        ? dto.timeEntries.map((entry) => ({
              id: entry?.id ?? null,
              version: entry?.version ?? null,
              date: entry?.date ?? "",
              amount:
                  entry?.amount !== undefined && entry?.amount !== null
                      ? String(entry.amount)
                      : "",
              start: entry?.startTime ?? "",
              end: entry?.endTime ?? "",
              articleId: entry?.articleId ?? "",
              articleName: entry?.articleName ?? "",
              invoiced: entry?.invoiced ?? false,
          }))
        : [],
    materials: Array.isArray(dto?.materialEntries)
        ? dto.materialEntries.map((entry) => ({
              id: entry?.id ?? null,
              version: entry?.version ?? null,
              amount:
                  entry?.amount !== undefined && entry?.amount !== null
                      ? String(entry.amount)
                      : "",
              articleId: entry?.articleId ?? "",
              articleName: entry?.articleName ?? "",
              invoiced: entry?.invoiced ?? false,
          }))
        : [],
});

export const buildOrderPayload = (order, { includeEntries = true } = {}) => {
    const payload = {
        title: order.title.trim(),
        customerId: order.customerId,
        timeEntries: includeEntries
            ? order.performanceDays.map((entry) => {
                  const amount = toNum(entry.amount);
                  const resolvedAmount = amount > 0 ? amount : durationHours(entry.start, entry.end);
                  return {
                      id: entry.id || undefined,
                      version:
                          entry.version !== null && entry.version !== undefined
                              ? entry.version
                              : undefined,
                      date: entry.date || undefined,
                      amount: resolvedAmount,
                      startTime: entry.start || undefined,
                      endTime: entry.end || undefined,
                      articleId: entry.articleId || undefined,
                  };
              })
            : [],
        materialEntries: includeEntries
            ? order.materials.map((entry) => ({
                  id: entry.id || undefined,
                  version:
                      entry.version !== null && entry.version !== undefined
                          ? entry.version
                          : undefined,
                  amount: toNum(entry.amount),
                  articleId: entry.articleId || undefined,
              }))
            : [],
    };

    if (order.id) {
        payload.id = order.id;
    }
    if (order.version !== null && order.version !== undefined) {
        payload.version = order.version;
    }

    return payload;
};

export const orderTotals = (order) => {
    const hours = order.performanceDays.reduce(
        (sum, day) => sum + (toNum(day.amount) || durationHours(day.start, day.end)),
        0
    );
    const materials = order.materials.reduce((sum, material) => sum + toNum(material.amount), 0);
    return { hours, materials, total: 0 };
};

export const resolveInvoiceStatus = (order) => {
    const performanceEntries = Array.isArray(order?.performanceDays) ? order.performanceDays : [];
    const materialEntries = Array.isArray(order?.materials) ? order.materials : [];
    const totalEntries = performanceEntries.length + materialEntries.length;
    if (totalEntries === 0) return "none";
    const invoicedCount =
        performanceEntries.filter((entry) => entry?.invoiced).length +
        materialEntries.filter((entry) => entry?.invoiced).length;
    if (invoicedCount === 0) return "none";
    if (invoicedCount === totalEntries) return "all";
    return "partial";
};

export const invoiceStatusStyles = {
    none: {
        border: "1px solid #f1b3b3",
        background: "linear-gradient(0deg, #fff0f0 0%, #fff 60%)",
    },
    partial: {
        border: "1px solid #ffd6a0",
        background: "linear-gradient(0deg, #fff3d6 0%, #fff 60%)",
    },
    all: {
        border: "1px solid #b7e4c7",
        background: "linear-gradient(0deg, #eefaf1 0%, #fff 60%)",
    },
};

export const resolvePerformanceAmount = (entry) => {
    const amount = toNum(entry.amount);
    if (amount > 0) return amount;
    const hours = durationHours(entry.start, entry.end);
    return hours > 0 ? hours : 0;
};
